import { useEffect, useRef, useState } from 'react';
import {
  PhuongThucThanhToan,
  PhuongThucThanhToanInput,
  PhuongThucThanhToanResponse
} from '@/types/schemas/phuong-thuc-thanh-toan.type';
import { useAuth } from '@/contexts/auth-context';
import api from '@/lib/api';

interface UsePhuongThucThanhToanReturn {
  paymentMethods: PhuongThucThanhToan[];
  isLoading: boolean;
  addPaymentMethod: (newPaymentMethod: PhuongThucThanhToanInput) => Promise<void>;
  updatePaymentMethod: (uuid: string, updatedPaymentMethod: PhuongThucThanhToanInput) => Promise<void>;
  deletePaymentMethod: (uuid: string) => Promise<void>;
  refreshPaymentMethods: () => Promise<void>;
}

export const usePhuongThucThanhToan = (
  initialPaymentMethods: PhuongThucThanhToan[] = []
): UsePhuongThucThanhToanReturn => {
  const [paymentMethods, setPaymentMethods] = useState<PhuongThucThanhToan[]>(initialPaymentMethods);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const isFetchingRef = useRef(false);

  const { entity } = useAuth();

  const fetchPaymentMethods = async () => {
    if (!entity?.slug || isFetchingRef.current) return;

    isFetchingRef.current = true;
    setIsLoading(true);
    try {
      const response = await api.get<PhuongThucThanhToanResponse>(`/entities/${entity.slug}/erp/payment-methods/`);
      setPaymentMethods(response.data.results);
    } catch (error) {
      console.error('Error fetching payment methods:', error);
    } finally {
      setIsLoading(false);
      isFetchingRef.current = false;
    }
  };

  const addPaymentMethod = async (newPaymentMethod: PhuongThucThanhToanInput) => {
    if (!entity?.slug) return;

    setIsLoading(true);
    try {
      const response = await api.post(`/entities/${entity.slug}/erp/payment-methods/`, {
        ma_pttt: newPaymentMethod.ma_pttt,
        ten_pttt: newPaymentMethod.ten_pttt,
        ten_pttt2: newPaymentMethod.ten_pttt2 === null ? '' : newPaymentMethod.ten_pttt2,
        ma_ph_thuc_hddt: newPaymentMethod.ma_ph_thuc_hddt,
        status: newPaymentMethod.status
      });

      const addedPaymentMethod: PhuongThucThanhToan = response.data;
      setPaymentMethods(prev => [...prev, addedPaymentMethod]);
    } catch (error) {
      console.error('Error adding payment method:', error);
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  const updatePaymentMethod = async (uuid: string, updatedPaymentMethod: PhuongThucThanhToanInput) => {
    if (!entity?.slug) return;

    setIsLoading(true);
    try {
      const response = await api.put(`/entities/${entity.slug}/erp/payment-methods/${uuid}/`, {
        ma_pttt: updatedPaymentMethod.ma_pttt,
        ten_pttt: updatedPaymentMethod.ten_pttt,
        ten_pttt2: updatedPaymentMethod.ten_pttt2 === null ? '' : updatedPaymentMethod.ten_pttt2,
        ma_ph_thuc_hddt: updatedPaymentMethod.ma_ph_thuc_hddt,
        status: updatedPaymentMethod.status
      });

      // Update the payment method in the list
      const updatedPaymentMethodData: PhuongThucThanhToan = response.data;

      setPaymentMethods(prev =>
        prev.map(method => (method.uuid === updatedPaymentMethodData.uuid ? updatedPaymentMethodData : method))
      );
    } catch (error) {
      console.error('Error updating payment method:', error);
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  const deletePaymentMethod = async (uuid: string) => {
    if (!entity?.slug) return;

    setIsLoading(true);
    try {
      await api.delete(`/entities/${entity.slug}/erp/payment-methods/${uuid}/`);

      // Remove the payment method from the list
      setPaymentMethods(prev => prev.filter(method => method.uuid !== uuid));
    } catch (error) {
      console.error('Error deleting payment method:', error);
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchPaymentMethods();
  }, [entity?.slug]);

  return {
    paymentMethods,
    isLoading,
    addPaymentMethod,
    updatePaymentMethod,
    deletePaymentMethod,
    refreshPaymentMethods: fetchPaymentMethods
  };
};
